import { Helmet } from "react-helmet-async";
import { ORG_JSONLD, WEBSITE_JSONLD } from "../data/seo";

const SITE_URL = "https://yellowvideoproduction.com";
const DEFAULT_OG = `${SITE_URL}/yellow-logo.png`;

/**
 * Per-page SEO component.
 *  - title         : Page <title> (Google shows ~60 chars)
 *  - description   : Meta description (Google shows ~155 chars)
 *  - path          : Canonical path (e.g. "/photography-liverpool")
 *  - keywords      : Comma-separated keywords (low SEO weight but harmless)
 *  - ogImage       : Optional social-share image URL
 *  - jsonLd        : Optional JSON-LD object or array for structured data
 *  - noIndex       : Set true on confirmation pages to keep them out of search
 *  - breadcrumbs   : Optional [{name, path}] for automatic BreadcrumbList schema
 */
export default function SEO({ title, description, path = "/", keywords, ogImage, jsonLd, noIndex = false, breadcrumbs }) {
  const url = `${SITE_URL}${path}`;
  const image = ogImage || DEFAULT_OG;

  // Build a graph of JSON-LD blocks so multiple structured data types coexist cleanly.
  // Organisation + WebSite injected on EVERY page so Google sees a consistent
  // brand graph regardless of which URL gets crawled.
  const ldBlocks = [ORG_JSONLD, WEBSITE_JSONLD];
  if (jsonLd) ldBlocks.push(...(Array.isArray(jsonLd) ? jsonLd : [jsonLd]));
  if (breadcrumbs && breadcrumbs.length > 0) {
    ldBlocks.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((b, idx) => ({
        "@type": "ListItem",
        position: idx + 1,
        name: b.name,
        item: `${SITE_URL}${b.path}`,
      })),
    });
  }

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Yellow Video Production Liverpool" />
      <meta property="og:locale" content="en_GB" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Geo / locality hints */}
      <meta name="geo.region" content="GB-LIV" />
      <meta name="geo.placename" content="Liverpool" />
      <meta name="geo.position" content="53.4084;-2.9916" />
      <meta name="ICBM" content="53.4084, -2.9916" />

      {ldBlocks.map((block, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(block)}</script>
      ))}
    </Helmet>
  );
}
